// Activity metrics store for organization activity insights
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";

import { isHttpExceptionModel, type types, useServices } from "@/lib/openapi";

export type OrganizationActivity = types.OrganizationActivityResponse;
export type UserActivity = types.UserActivityResponse;
export type ActivityReport = types.OrganizationActivityReportResponse;

export const useActivityMetricsStore = defineStore("activityMetrics", () => {
    const toast = useToast();
    const activityReport = ref<ActivityReport | null>(null);
    const isLoading = ref(false);
    const currentError = ref<string | null>(null);

    const api = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: true,
            organization: false, // Platform-wide, not org-scoped
            stek: false,
        },
    });

    const getActivityReport = async (
        sinceDays: number = 30,
        includeUserDetails: boolean = true
    ) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } =
                await api.startOrganizationActivityMetricsGetOrganizationActivityStartGet(
                    {
                        query: {
                            since_days: sinceDays,
                            include_user_details: includeUserDetails,
                        },
                    }
                );

            if (!response?.ok || !data) {
                currentError.value = "Failed to load activity metrics";
                return null;
            }

            activityReport.value = data;
            return data;
        } catch (error) {
            handleError(error, "Failed to load activity metrics");
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    const handleError = (error: unknown, defaultMessage: string) => {
        if (isHttpExceptionModel(error)) {
            const serverMessage = error.detail[0]?.message || defaultMessage;
            currentError.value = serverMessage;
            toast.add({
                severity: "error",
                summary: serverMessage,
                life: 5000,
            });
        } else {
            currentError.value = defaultMessage;
            toast.add({
                severity: "error",
                summary: defaultMessage,
                life: 5000,
            });
        }
        console.error(error);
    };

    return {
        activityReport,
        isLoading,
        currentError,
        getActivityReport,
    };
});

export const useActivityMetricsManager = () =>
    storeToRefs(useActivityMetricsStore());
